import {
  type BoxProps,
  type IconButtonProps,
  Spacer,
  type StackProps,
  useBreakpointValue,
} from '@chakra-ui/react'
import { Flex, HStack, Icon, IconButton } from '@chakra-ui/react'
import { atom, useAtom } from 'jotai'
import { PanelRightClose, PanelRightOpen } from 'lucide-react'
import React, { ReactNode, useCallback, useEffect, useMemo } from 'react'
import { Tooltip } from '~/components/ui/tooltip'
import { useUserClickedOutsideElementBoundingBox } from '~/hooks/useUserClickedOutsideElementBoundingBox'

import { useDetailPanelStyle } from './componentStyles/DetailPanel.style'
import { useHeaderStyle } from './componentStyles/Header.style'
import {
  ListPanelVariants,
  useListPanelStyles,
} from './componentStyles/ListPanel.style'
import {
  ToggleListButtonVariants,
  useToggleListButtonStyles,
} from './componentStyles/ToggleListButton.style'

export const SlidingPanelBreak = { base: true, sm: false }

const togglePanelAtom = atom({
  isOpen: false,
  isDisabled: false,
  hasListPanel: false,
})

const TOGGLE_LIST_BUTTON_SELECTOR = '[data-toggle-list-button]'
interface UseTogglePanelProps {
  isDisabled?: boolean
}
export const useTogglePanel = ({
  isDisabled = false,
}: UseTogglePanelProps = {}) => {
  const [togglePanelState, setTogglePanelState] = useAtom(togglePanelAtom)

  if (isDisabled !== togglePanelState.isDisabled) {
    setTogglePanelState((prev) => {
      return { ...prev, isDisabled }
    })
  }

  const togglePanelVisibility = useCallback(
    (open?: boolean) => {
      if (isDisabled) {
        return
      }
      setTogglePanelState((prev) => {
        return { ...prev, isOpen: open ?? !prev.isOpen }
      })
    },
    [isDisabled, setTogglePanelState],
  )
  return { state: togglePanelState, togglePanelVisibility }
}

export const QuickActionStack = ({ children, ...rest }: StackProps) => {
  return (
    <HStack gap="0.75rem" paddingRight="1.5rem" {...rest}>
      {children}
    </HStack>
  )
}

type HeaderProps = Omit<BoxProps, 'direction'>
export const Header = ({ children, ...rest }: HeaderProps) => {
  const style = useHeaderStyle()
  return (
    <Flex css={style} {...rest}>
      {children}
    </Flex>
  )
}

type ToggleListButtonProps = {
  clickBehavior?: 'always-open' | 'always-close'
  hidden?: boolean
} & Omit<IconButtonProps, 'aria-label'>
export const ToggleListButton = ({
  hidden,
  clickBehavior,
  ...rest
}: ToggleListButtonProps) => {
  const [togglePanelState, setTogglePanelState] = useAtom(togglePanelAtom)
  const slidingPanelBreak = useBreakpointValue(SlidingPanelBreak, {
    ssr: false,
  })
  const shouldShow =
    togglePanelState.hasListPanel && slidingPanelBreak && !hidden

  const handleToggleClick = () => {
    if (togglePanelState.isDisabled) {
      return
    }
    switch (clickBehavior) {
      case 'always-open':
        setTogglePanelState((prev) => {
          return { ...prev, isOpen: true }
        })
        break
      case 'always-close':
        setTogglePanelState((prev) => {
          return { ...prev, isOpen: false }
        })
        break
      default:
        setTogglePanelState((prev) => {
          return { ...prev, isOpen: !prev.isOpen }
        })
        break
    }
  }
  let ToggleIcon: React.ComponentType<any>
  let tooltipText: string
  switch (clickBehavior) {
    case 'always-open':
      ToggleIcon = PanelRightOpen
      tooltipText = 'Show List'
      break
    case 'always-close':
      ToggleIcon = PanelRightClose
      tooltipText = 'Hide List'
      break
    default:
      ToggleIcon = togglePanelState.isOpen ? PanelRightClose : PanelRightOpen
      tooltipText = togglePanelState.isOpen ? 'Hide List' : 'Show List'
      break
  }
  if (togglePanelState.isDisabled) {
    ToggleIcon = PanelRightClose
    tooltipText = ''
  }
  const ariaText = `${tooltipText} View`

  const variant = togglePanelState.isDisabled
    ? ToggleListButtonVariants.Disabled
    : ToggleListButtonVariants.Default
  const styles = useToggleListButtonStyles({ variant })

  if (shouldShow) {
    return (
      <Flex
        css={styles.container}
        data-toggle-list-button=""
        onClick={handleToggleClick}
      >
        <Tooltip
          showArrow
          positioning={{ placement: 'right' }}
          content={tooltipText}
        >
          <IconButton
            css={{
              ...styles.iconButton,
              width: '100%',
              marginBottom: 0,
            }}
            {...rest}
            aria-label={ariaText}
          >
            <Icon css={styles.icon}>
              <ToggleIcon size="100%" />
            </Icon>
          </IconButton>
        </Tooltip>
      </Flex>
    )
  }

  return <></>
}

type StaticListPanelProps = Omit<BoxProps, 'direction'>
export const StaticListPanel = ({
  children,
  ...rest
}: StaticListPanelProps) => {
  const styles = useListPanelStyles()

  // Extract layout properties from staticContainer styles (Chakra v3 requirement)
  const containerLayoutProps = {
    flexDirection: styles.staticContainer?.flexDirection,
    flexShrink: styles.staticContainer?.flexShrink,
    alignItems: styles.staticContainer?.alignItems,
    width: styles.staticContainer?.width,
  }

  return (
    <Flex
      css={styles.staticContainer}
      {...containerLayoutProps}
      {...rest}
      backgroundColor="bg.default"
    >
      {children}
    </Flex>
  )
}

interface SlidingListPanelProps extends Omit<BoxProps, 'direction'> {
  isOpen: boolean
  onToggleVisible?: () => void
}
export const SlidingListPanel = ({
  isOpen,
  onToggleVisible,
  children,
  ...rest
}: SlidingListPanelProps) => {
  const flexRef = React.useRef<HTMLDivElement>(null)

  useUserClickedOutsideElementBoundingBox(
    flexRef.current,
    () => {
      if (!isOpen) {
        return
      }
      onToggleVisible?.()
    },
    (event) => {
      const target = event.target
      return (
        target instanceof Element &&
        Boolean(target.closest(TOGGLE_LIST_BUTTON_SELECTOR))
      )
    },
  )

  const styles = useListPanelStyles({
    variant: isOpen ? ListPanelVariants.Open : ListPanelVariants.Closed,
  })

  // Extract layout properties from slidingContainer styles (Chakra v3 requirement)
  const containerLayoutProps = {
    flexDirection: styles.slidingContainer?.flexDirection,
    shrink: styles.slidingContainer?.shrink,
    alignItems: styles.slidingContainer?.alignItems,
    width: styles.slidingContainer?.width,
    minWidth: styles.slidingContainer?.minWidth,
  }

  return (
    <Flex
      ref={flexRef}
      position="absolute"
      css={styles.slidingContainer}
      {...containerLayoutProps}
      {...rest}
    >
      {children}
    </Flex>
  )
}

export const DetailPanel = ({
  children,
  ...rest
}: Omit<BoxProps, 'direction'>) => {
  const style = useDetailPanelStyle()

  // Extract layout properties (Chakra v3 requirement)
  const layoutProps = {
    flexDirection: style?.flexDirection,
    flex: style?.flex,
  }

  return (
    <Flex
      css={style}
      {...layoutProps}
      borderLeft="2px solid"
      borderColor="border.default"
      {...rest}
    >
      {children}
    </Flex>
  )
}

interface ResponsivePageProps extends Omit<BoxProps, 'onToggle' | 'direction'> {
  header?: ReactNode
  quickActionButtons?: React.ReactElement[]
  listPanel: ReactNode | (() => ReactNode)
  listPanelProps?: Omit<BoxProps, 'direction'>
  detailPanel?: ReactNode | (() => ReactNode)
  onToggle?: (isOpen: boolean) => void
  listPanelAlwaysOpen?: boolean
}
export const ResponsivePage = ({
  header,
  quickActionButtons,
  listPanel,
  listPanelProps,
  detailPanel,
  onToggle,
  listPanelAlwaysOpen,
  ...rest
}: ResponsivePageProps) => {
  const slidingPanelBreak = useBreakpointValue(SlidingPanelBreak, {
    ssr: false,
  })
  const [togglePanelState, setTogglePanelState] = useAtom(togglePanelAtom)

  useEffect(() => {
    setTogglePanelState((prev) => ({ ...prev, hasListPanel: true }))
    return () => {
      setTogglePanelState((prev) => ({ ...prev, hasListPanel: false }))
    }
  }, [setTogglePanelState])

  const handleToggleClick = () => {
    const newOpenState = !togglePanelState.isOpen
    setTogglePanelState((prev) => {
      return { ...prev, isOpen: newOpenState }
    })
    onToggle?.(newOpenState)
  }

  const listPanelContent = useMemo(() => {
    if (typeof listPanel === 'function') {
      return listPanel()
    }

    return listPanel
  }, [listPanel])

  const detailPanelContent = useMemo(() => {
    if (typeof detailPanel === 'function') {
      return detailPanel()
    }

    return detailPanel
  }, [detailPanel])

  const shouldShowStaticPanel = listPanelAlwaysOpen || !slidingPanelBreak

  return (
    <Flex width="full" position="relative" {...rest}>
      {shouldShowStaticPanel && (
        <StaticListPanel {...listPanelProps}>
          {listPanelContent}
        </StaticListPanel>
      )}
      {!shouldShowStaticPanel && (
        <SlidingListPanel
          isOpen={togglePanelState.isOpen}
          onToggleVisible={handleToggleClick}
          {...listPanelProps}
        >
          {listPanelContent}
        </SlidingListPanel>
      )}
      <DetailPanel>
        {header && (
          <Header>
            {header}
            <Spacer />
            {quickActionButtons && (
              <QuickActionStack>
                {quickActionButtons.map((qab) => {
                  return qab
                })}
              </QuickActionStack>
            )}
          </Header>
        )}
        {detailPanelContent}
      </DetailPanel>
    </Flex>
  )
}
